import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Guerriero (manuale V1, rr. 3399-3485).
 *
 * - **3 mosse automatiche** (`type: 'starting'`): *Piegare Sbarre, Sollevare
 *   Saracinesche*, *Corazzato*, *Arma Prescelta*.
 * - **2 mosse di scelta** (`type: 'starting'`, `pickCount: 1` nel gruppo
 *   `startingChoiceGroups`): *Parata*, *Tecniche Segrete di Combattimento*.
 *
 * **Caso peculiare** *Arma Prescelta* (manuale rr. 3418-3463): la mossa
 * richiede al giocatore 3 sotto-scelte indipendenti (tipologia di base + portata
 * + 2 miglioramenti), ognuna con pool diverso. Lo schema attuale `MoveSchema.choices`
 * supporta UN solo gruppo di scelte, non 3 indipendenti. Soluzione adottata
 * (D2 del WIP): tutte le sotto-scelte vivono nella `description` testuale; la
 * modellazione strutturata è tracciata da FI-18 (`MoveSchema.choiceGroups`).
 */

const piegareSbarreSollevareSaracinesche: Move = {
  id: 'guerriero.piegare-sbarre-sollevare-saracinesche',
  name: 'Piegare Sbarre, Sollevare Saracinesche',
  type: 'starting',
  classId: 'guerriero',
  trigger: 'Quando usi la forza bruta per distruggere un oggetto inanimato',
  description:
    "Tira+**FOR**. Con un 10+, scegli tre opzioni. Con 7-9, scegline due:\n\n- non ci metti molto tempo;\n- niente di valore viene danneggiato;\n- non fai una grande quantità di rumore;\n- puoi riaggiustare l'oggetto senza troppa fatica.",
  roll: {
    stat: 'forza',
    on10plus: { text: 'Scegli tre opzioni dalla lista.' },
    on7to9: { text: 'Scegli due opzioni dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const corazzato: Move = {
  id: 'guerriero.corazzato',
  name: 'Corazzato',
  type: 'starting',
  classId: 'guerriero',
  trigger: 'In ogni momento (passiva)',
  description: "Ignori l'etichetta *ingombrante* dell'armatura che indossi.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const armaPrescelta: Move = {
  id: 'guerriero.arma-prescelta',
  name: 'Arma Prescelta',
  type: 'starting',
  classId: 'guerriero',
  trigger: 'Alla creazione del personaggio',
  description:
    'Questa è la **Tua Arma**. Ce ne sono tante che le somigliano, ma questa è la tua. La Tua Arma è la tua migliore amica. È la tua vita. Devi dominarla come domini la tua vita. Senza di te, la Tua Arma non è niente. Senza la Tua Arma, tu non sei niente.\n\nDescrivi la tua *Arma Prescelta*, ed eventualmente dalle un nome. Tutte le tipologie hanno peso 2.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18: 3 gruppi di scelta indipendenti + 2 campi narrativi setup.
  choiceGroups: [
    {
      id: 'tipologia',
      label: 'Tipologia',
      description: 'Scegli la tipologia base della tua arma (peso 2).',
      options: [
        { id: 'spada', label: 'Spada' },
        { id: 'ascia', label: 'Ascia' },
        { id: 'martello', label: 'Martello' },
        { id: 'lancia', label: 'Lancia' },
        { id: 'mazza', label: 'Mazza' },
        { id: 'pugnale', label: 'Pugnale' },
        { id: 'guanti', label: 'Guanti da combattimento' },
      ],
      pickCount: 1,
    },
    {
      id: 'portata',
      label: 'Portata',
      description: 'Scegli la portata che meglio si adatta alla Tua Arma.',
      options: [
        { id: 'corta', label: 'Corta' },
        { id: 'media', label: 'Media' },
        { id: 'lunga', label: 'Lunga' },
      ],
      pickCount: 1,
    },
    {
      id: 'miglioramenti',
      label: 'Miglioramenti',
      description: 'Scegli due miglioramenti per la Tua Arma.',
      options: [
        {
          id: 'punte-e-uncini',
          label: 'Punte e uncini',
          description: '+1 danni, *brutale*, +1 peso.',
        },
        { id: 'affilata', label: 'Affilata', description: "Aggiungi l'etichetta *+2 perforante*." },
        {
          id: 'perfettamente-bilanciata',
          label: 'Perfettamente bilanciata',
          description: "Aggiungi l'etichetta *precisa*.",
        },
        { id: 'bordi-seghettati', label: 'Bordi seghettati', description: '+1 danni.' },
        {
          id: 'brilla-creatura',
          label: 'Brilla in presenza di un tipo di creatura',
          description: 'A tua scelta.',
        },
        { id: 'pesante', label: 'Pesante', description: '+1 danni, *impatto*, +1 peso.' },
        {
          id: 'sinistra',
          label: 'Sinistra',
          description: '+1 al prossimo tiro dopo aver ucciso un nemico.',
        },
        {
          id: 'antica',
          label: 'Antica',
          description: 'Vedi le mosse avanzate *Cimelio* e *Settimo Senso*.',
        },
        {
          id: 'versatile',
          label: 'Versatile',
          description: 'Scegli una portata aggiuntiva.',
        },
        { id: 'ben-costruita', label: 'Ben costruita', description: '-1 peso.' },
      ],
      pickCount: 2,
    },
  ],
  narrativeFields: [
    {
      id: 'descrizione-arma',
      label: 'Descrivi la Tua Arma',
      placeholder: 'Una vecchia spada di famiglia, lama lucente con incisioni rune…',
      required: true,
      multiline: true,
    },
    {
      id: 'nome-arma',
      label: "Nome dell'arma (opzionale)",
      placeholder: 'es. Vendichiana, Spaccaossa…',
      required: false,
      multiline: false,
    },
  ],
};

const parata: Move = {
  id: 'guerriero.parata',
  name: 'Parata',
  type: 'starting',
  classId: 'guerriero',
  trigger: "Quando provi a parare un attacco nemico con l'arma che stai brandendo",
  description:
    "Tira+**modificatore alla Caratteristica** che useresti per fare *taglia e spacca* con essa. Con 10+, scegli 2. Con 7-9, scegli 1:\n\n- non subisci danno;\n- sfrutti un varco che crei nella difesa avversaria per infliggere danni pari al tuo dado di Danno (solo il dado, senza applicare altri bonus o etichette);\n- incroci le lame con lui e diventi il suo unico bersaglio, almeno per un po'.\n\nNe hai viste troppe per non saperlo, vecchio mio: anche se infliggi danni con una *Parata*, ciò *non significa* che stai compiendo un *attacco* contro il tuo avversario!",
  // NB: il roll usa la stat dell'arma brandita; la stat è dinamica.
  // Lo schema attuale richiede una stat fissa per `roll.stat`. Workaround:
  // la stat viene scelta a runtime; per la rappresentazione statica usiamo
  // 'forza' come default rappresentativo (è la stat più comune per le armi
  // da mischia), e la regola "modificatore alla Caratteristica dell'arma"
  // resta nella description.
  roll: {
    stat: 'forza',
    on10plus: {
      text: 'Scegli 2 opzioni dalla lista (parare, infliggere danni dado, incrociare lame).',
    },
    on7to9: { text: 'Scegli 1 opzione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const tecnicheSegreteDiCombattimento: Move = {
  id: 'guerriero.tecniche-segrete-di-combattimento',
  name: 'Tecniche Segrete di Combattimento',
  type: 'starting',
  classId: 'guerriero',
  trigger:
    'Quando fai 10+ su un tiro di *taglia e spacca* e scegli di lasciare che il nemico risponda comunque',
  description:
    'Invece di infliggere i consueti +1d6 danni come previsto dalla mossa, scegli uno:\n\n- *colpo a tradimento*: infliggi i normali +1d6 danni;\n- *lacrime agli occhi*: lo accechi, assordi o disorienti in altro modo;\n- *finta*: lo fai esporre e prendi +1 al prossimo tiro contro di lui;\n- *sgambetto*: miri basso e gli rendi il movimento più difficoltoso.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 */
export const guerrieroStartingAutoMoves: readonly Move[] = [
  piegareSbarreSollevareSaracinesche,
  corazzato,
  armaPrescelta,
] as const;

/**
 * Mosse iniziali di SCELTA (2): il giocatore ne sceglie una al livello 1.
 */
export const guerrieroStartingChoiceMoves: readonly Move[] = [
  parata,
  tecnicheSegreteDiCombattimento,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const guerrieroInitialMoves: readonly Move[] = [
  ...guerrieroStartingAutoMoves,
  ...guerrieroStartingChoiceMoves,
];
